import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useLanguage } from "@/context/LanguageContext";
import SearchableSelect from "@/components/SearchableSelect";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import SEO from "@/components/SEO";
import { API } from "@/config/api";
import { readPropertySearchParams } from "@/utils/propertySearchParams";
import { submitSearchInquiry } from "@/utils/submitSearchInquiry";

/**
 * RechercherPage — Advanced search as lead capture (not a listing filter).
 * Collects criteria + contact details and saves them in the admin panel.
 */
const RechercherPage = () => {
  const [searchParams] = useSearchParams();
  const { language } = useLanguage();

  const initialFilters = readPropertySearchParams(searchParams);

  const [step, setStep] = useState("criteria"); // criteria | contact | success
  const [submitting, setSubmitting] = useState(false);
  const [searchMode, setSearchMode] = useState("buy");
  const [filters, setFilters] = useState({
    location: initialFilters.location,
    maxPrice: initialFilters.maxPrice,
    pieces: "",
    minSurface: initialFilters.minSurface,
    propertyType: initialFilters.propertyType,
    bedrooms: initialFilters.minBedrooms,
  });
  const [contact, setContact] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [propertyTypes, setPropertyTypes] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const typesRes = await axios.get(`${API}/property-types`);
        setPropertyTypes(typesRes.data);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  const buyPriceRanges = [
    { value: "500000", label: "500'000 CHF" },
    { value: "1000000", label: "1'000'000 CHF" },
    { value: "2000000", label: "2'000'000 CHF" },
    { value: "3000000", label: "3'000'000 CHF" },
    { value: "5000000", label: "5'000'000 CHF" },
    { value: "10000000", label: "10'000'000 CHF" },
    { value: "10000001", label: "10'000'000+ CHF" },
  ];

  const rentPriceRanges = [
    { value: "2000", label: "< CHF 2'000 / mois" },
    { value: "5000", label: "< CHF 5'000 / mois" },
    { value: "10000", label: "< CHF 10'000 / mois" },
    { value: "15000", label: "< CHF 15'000 / mois" },
    { value: "20000", label: "< CHF 20'000 / mois" },
    { value: "35000", label: "< CHF 35'000 / mois" },
    { value: "35001", label: "> CHF 35'000 / mois" },
  ];

  const priceRanges = searchMode === "buy" ? buyPriceRanges : rentPriceRanges;

  const piecesOptions = [
    { value: "2", label: "2+" },
    { value: "2.5", label: "2.5+" },
    { value: "3", label: "3+" },
    { value: "3.5", label: "3.5+" },
    { value: "4", label: "4+" },
    { value: "4.5", label: "4.5+" },
    { value: "5", label: "5+" },
    { value: "5.5", label: "5.5+" },
    { value: "6", label: "6+" },
    { value: "7", label: "7+" },
  ];

  const surfaceOptions = [
    { value: "50", label: "50 m²" },
    { value: "100", label: "100 m²" },
    { value: "150", label: "150 m²" },
    { value: "200", label: "200 m²" },
    { value: "300", label: "300 m²" },
    { value: "500", label: "500 m²" },
  ];

  const bedroomOptions = [
    { value: "1", label: "1+" },
    { value: "2", label: "2+" },
    { value: "3", label: "3+" },
    { value: "4", label: "4+" },
    { value: "5", label: "5+" },
  ];

  const propertyTypeOptions =
    propertyTypes.length > 0
      ? propertyTypes.map((type) => ({ value: type, label: type }))
      : [
          { value: "villa", label: "Villa" },
          { value: "apartment", label: language === "fr" ? "Appartement" : "Apartment" },
          { value: "penthouse", label: "Penthouse" },
          {
            value: "townhouse",
            label: language === "fr" ? "Maison de ville" : "Townhouse",
          },
        ];

  const fieldClass =
    "w-full px-0 py-3 border-b border-[#E5E5E0] text-[#1A1A1A] placeholder:text-[#999] focus:outline-none focus:border-[#8B7355] transition-colors text-sm font-light bg-transparent";

  const handleCriteriaContinue = (e) => {
    e.preventDefault();
    setStep("contact");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (
      !contact.first_name.trim() ||
      !contact.last_name.trim() ||
      !contact.email.trim() ||
      !contact.phone.trim()
    ) {
      toast.error(
        language === "fr"
          ? "Veuillez remplir tous les champs obligatoires"
          : "Please fill in all required fields"
      );
      return;
    }

    setSubmitting(true);
    try {
      await submitSearchInquiry({
        searchType: "recherche",
        sourcePage: "/rechercher",
        searchMode: searchMode === "buy" ? "buy" : "rent",
        filters,
        contact,
      });
      setStep("success");
      toast.success(
        language === "fr"
          ? "Votre demande a bien été envoyée"
          : "Your enquiry has been sent"
      );
    } catch (error) {
      const detail = error.response?.data?.detail;
      const fallback = language === "fr" ? "Échec de l'envoi" : "Failed to send enquiry";
      toast.error(typeof detail === "string" ? detail : fallback);
    } finally {
      setSubmitting(false);
    }
  };

  const criteriaSummary = [
    searchMode === "buy"
      ? language === "fr"
        ? "Achat"
        : "Buy"
      : language === "fr"
        ? "Location"
        : "Rent",
    filters.location,
    filters.maxPrice
      ? `${language === "fr" ? "Budget max." : "Max budget"}: ${
          priceRanges.find((p) => p.value === filters.maxPrice)?.label || filters.maxPrice
        }`
      : null,
    filters.pieces
      ? `${language === "fr" ? "Pièces" : "Rooms"}: ${filters.pieces}+`
      : null,
    filters.minSurface ? `${filters.minSurface}+ m²` : null,
    filters.propertyType,
    filters.bedrooms
      ? `${filters.bedrooms}+ ${language === "fr" ? "chambres" : "bedrooms"}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <main data-testid="rechercher-page" className="min-h-screen bg-byron-bg">
      <SEO
        title="Rechercher"
        description="Soumettez vos critères de recherche et vos coordonnées. Notre équipe vous proposera des biens adaptés, y compris hors marché."
        canonical="/rechercher"
      />
      <div className="header-spacer" />

      <section className="py-10 md:py-14">
        <div className="container-byron">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-2xl mx-auto text-center"
          >
            <p className="label-text mb-4">
              {language === "fr" ? "Recherche avancée" : "Advanced search"}
            </p>
            <h1 className="heading-primary mb-6">
              {language === "fr" ? "Trouvez votre bien idéal" : "Find your ideal property"}
            </h1>
            <p className="body-text">
              {language === "fr"
                ? "Indiquez vos critères et vos coordonnées. Notre équipe vous recontactera avec une sélection personnalisée — y compris des biens hors marché."
                : "Share your criteria and contact details. Our team will follow up with a tailored selection — including off-market properties."}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="pb-20 md:pb-28 bg-byron-bg" data-testid="search-form-section">
        <div className="container-byron">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-white border border-[#E8E6E1] p-6 md:p-10">
              {step === "success" ? (
                <div className="text-center py-10" data-testid="search-success">
                  <div className="w-12 h-12 rounded-full bg-[#1A1A1A] text-white flex items-center justify-center mx-auto mb-5">
                    <Check size={22} strokeWidth={1.5} />
                  </div>
                  <h2 className="heading-secondary mb-3">
                    {language === "fr" ? "Merci pour votre demande" : "Thank you for your enquiry"}
                  </h2>
                  <p className="body-text max-w-md mx-auto">
                    {language === "fr"
                      ? "Nous avons bien reçu vos critères. Un conseiller Byron vous recontactera sous peu."
                      : "We have received your criteria. A Byron advisor will contact you shortly."}
                  </p>
                </div>
              ) : step === "criteria" ? (
                <form onSubmit={handleCriteriaContinue}>
                  <div className="flex items-center justify-center gap-6 mb-8 pb-6 border-b border-[#E5E5E0]">
                    <button
                      type="button"
                      onClick={() => {
                        setSearchMode("buy");
                        setFilters({ ...filters, maxPrice: "" });
                      }}
                      className={`text-sm uppercase tracking-[0.15em] transition-colors pb-2 ${
                        searchMode === "buy"
                          ? "text-[#1A1A1A] border-b-2 border-[#1A1A1A]"
                          : "text-[#999] hover:text-[#666]"
                      }`}
                      data-testid="search-mode-buy"
                    >
                      {language === "fr" ? "Acheter" : "Buy"}
                    </button>
                    <span className="text-[#DDD]">|</span>
                    <button
                      type="button"
                      onClick={() => {
                        setSearchMode("rent");
                        setFilters({ ...filters, maxPrice: "" });
                      }}
                      className={`text-sm uppercase tracking-[0.15em] transition-colors pb-2 ${
                        searchMode === "rent"
                          ? "text-[#1A1A1A] border-b-2 border-[#1A1A1A]"
                          : "text-[#999] hover:text-[#666]"
                      }`}
                      data-testid="search-mode-rent"
                    >
                      {language === "fr" ? "Louer" : "Rent"}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 mb-6">
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.15em] text-[#888] block mb-2">
                        {language === "fr" ? "Localisation" : "Location"}
                      </label>
                      <AddressAutocomplete
                        value={filters.location}
                        onChange={(val) => setFilters({ ...filters, location: val })}
                        placeholder={
                          language === "fr"
                            ? "Ville, région, code postal..."
                            : "City, region, zip code..."
                        }
                        variant="compact"
                        types={["geocode"]}
                        preferLocality
                        iconSize={16}
                        testId="search-location"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] uppercase tracking-[0.15em] text-[#888] block mb-2">
                        {searchMode === "buy"
                          ? language === "fr"
                            ? "Budget maximum"
                            : "Maximum budget"
                          : language === "fr"
                            ? "Loyer mensuel max."
                            : "Max. monthly rent"}
                      </label>
                      <SearchableSelect
                        value={filters.maxPrice}
                        onChange={(val) => setFilters({ ...filters, maxPrice: val })}
                        options={priceRanges}
                        placeholder={
                          searchMode === "buy"
                            ? language === "fr"
                              ? "Sélectionner un budget"
                              : "Select budget"
                            : language === "fr"
                              ? "Sélectionner un loyer"
                              : "Select rent"
                        }
                        inputClassName={fieldClass}
                        data-testid="search-budget"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] uppercase tracking-[0.15em] text-[#888] block mb-2">
                        {language === "fr" ? "Pièces" : "Rooms"}
                      </label>
                      <SearchableSelect
                        value={filters.pieces}
                        onChange={(val) => setFilters({ ...filters, pieces: val })}
                        options={piecesOptions}
                        placeholder={
                          language === "fr" ? "Nombre de pièces" : "Number of rooms"
                        }
                        inputClassName={fieldClass}
                        data-testid="search-pieces"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] uppercase tracking-[0.15em] text-[#888] block mb-2">
                        {language === "fr" ? "Surface minimum" : "Minimum surface"}
                      </label>
                      <SearchableSelect
                        value={filters.minSurface}
                        onChange={(val) => setFilters({ ...filters, minSurface: val })}
                        options={surfaceOptions}
                        placeholder={
                          language === "fr" ? "Sélectionner une surface" : "Select surface"
                        }
                        inputClassName={fieldClass}
                        data-testid="search-surface"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] uppercase tracking-[0.15em] text-[#888] block mb-2">
                        {language === "fr" ? "Type de bien" : "Property type"}
                      </label>
                      <SearchableSelect
                        value={filters.propertyType}
                        onChange={(val) => setFilters({ ...filters, propertyType: val })}
                        options={propertyTypeOptions}
                        placeholder={language === "fr" ? "Tous les types" : "All types"}
                        inputClassName={fieldClass}
                        data-testid="search-property-type"
                      />
                    </div>

                    <div>
                      <label className="text-[10px] uppercase tracking-[0.15em] text-[#888] block mb-2">
                        {language === "fr" ? "Chambres" : "Bedrooms"}
                      </label>
                      <SearchableSelect
                        value={filters.bedrooms}
                        onChange={(val) => setFilters({ ...filters, bedrooms: val })}
                        options={bedroomOptions}
                        placeholder={
                          language === "fr" ? "Nombre de chambres" : "Number of bedrooms"
                        }
                        inputClassName={fieldClass}
                        data-testid="search-bedrooms"
                      />
                    </div>
                  </div>

                  <div className="text-center pt-6">
                    <button
                      type="submit"
                      className="inline-flex items-center gap-3 px-12 py-4 bg-[#1A1A1A] text-white text-[11px] uppercase tracking-[0.2em] hover:bg-[#333] transition-colors"
                      data-testid="search-submit"
                    >
                      {language === "fr" ? "Continuer" : "Continue"}
                      <ArrowRight size={14} />
                    </button>
                    <p className="text-xs text-[#999] mt-4">
                      {language === "fr"
                        ? "Étape suivante : vos coordonnées"
                        : "Next step: your contact details"}
                    </p>
                  </div>
                </form>
              ) : (
                <form onSubmit={handleSubmit} data-testid="search-contact-form">
                  <div className="flex items-center justify-between mb-8 pb-6 border-b border-[#E5E5E0]">
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.15em] text-[#888] mb-2">
                        {language === "fr" ? "Vos critères" : "Your criteria"}
                      </p>
                      <p className="text-sm text-[#1A1A1A] font-light">{criteriaSummary}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => setStep("criteria")}
                      className="text-xs uppercase tracking-[0.12em] text-[#8B7355] hover:text-[#1A1A1A] transition-colors whitespace-nowrap ml-4"
                    >
                      {language === "fr" ? "Modifier" : "Edit"}
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5 mb-6">
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.15em] text-[#888] block mb-2">
                        {language === "fr" ? "Prénom" : "First name"}
                      </label>
                      <input
                        type="text"
                        required
                        value={contact.first_name}
                        onChange={(e) => setContact({ ...contact, first_name: e.target.value })}
                        className={fieldClass}
                        data-testid="search-first-name"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.15em] text-[#888] block mb-2">
                        {language === "fr" ? "Nom" : "Last name"}
                      </label>
                      <input
                        type="text"
                        required
                        value={contact.last_name}
                        onChange={(e) => setContact({ ...contact, last_name: e.target.value })}
                        className={fieldClass}
                        data-testid="search-last-name"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.15em] text-[#888] block mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        required
                        value={contact.email}
                        onChange={(e) => setContact({ ...contact, email: e.target.value })}
                        className={fieldClass}
                        data-testid="search-email"
                      />
                    </div>
                    <div>
                      <label className="text-[10px] uppercase tracking-[0.15em] text-[#888] block mb-2">
                        {language === "fr" ? "Téléphone" : "Phone"}
                      </label>
                      <input
                        type="tel"
                        required
                        value={contact.phone}
                        onChange={(e) => setContact({ ...contact, phone: e.target.value })}
                        className={fieldClass}
                        data-testid="search-phone"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-[10px] uppercase tracking-[0.15em] text-[#888] block mb-2">
                        {language === "fr" ? "Message (optionnel)" : "Message (optional)"}
                      </label>
                      <textarea
                        rows={4}
                        value={contact.message}
                        onChange={(e) => setContact({ ...contact, message: e.target.value })}
                        placeholder={
                          language === "fr"
                            ? "Précisez vos attentes ou toute information utile..."
                            : "Share preferences or any useful details..."
                        }
                        className="w-full px-0 py-3 border-b border-[#E5E5E0] text-[#1A1A1A] placeholder:text-[#999] focus:outline-none focus:border-[#8B7355] transition-colors text-sm font-light bg-transparent resize-none"
                        data-testid="search-message"
                      />
                    </div>
                  </div>

                  <div className="text-center pt-6">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="inline-flex items-center gap-3 px-12 py-4 bg-[#1A1A1A] text-white text-[11px] uppercase tracking-[0.2em] hover:bg-[#333] transition-colors disabled:opacity-60"
                      data-testid="search-enquiry-submit"
                    >
                      {submitting
                        ? language === "fr"
                          ? "Envoi..."
                          : "Sending..."
                        : language === "fr"
                          ? "Envoyer ma demande"
                          : "Send enquiry"}
                      {!submitting && <ArrowRight size={14} />}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default RechercherPage;
